import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Download } from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { fetchRatings,
  saveRatings } from "../api";
import * as XLSX from "xlsx";
import {
  useNotification,
  isPermissionDenied,
  getPermissionDeniedMessage,
  notifyPermissionError,
} from '@1823-partners/core';

dayjs.extend(utc);

const EDITABLE_FIELDS = ["SPREAD"];

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = dayjs.utc(dateString);
  return d.isValid() ? d.format("MM/DD/YYYY") : dateString;
};

const formatUserName = (email) => {
  if (!email) return "";
  if (email.includes("@")) {
    const localPart = email.split("@")[0];
    return localPart
      .split(/[._]/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }
  return email;
};

export default function Ratings({ user }) {
  const { showNotification } = useNotification();
  const [ratings, setRatings] = useState([]);
  const [originalRatings, setOriginalRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [changedRows, setChangedRows] = useState([]);

  // Simple inline editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, field }
  const [editValue, setEditValue] = useState("");

  const loadRatings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchRatings();
      console.log("fetchRatings response:", response);
      // Handle various response formats: array, { data: [...] }, or { ratings: [...] }
      const data = Array.isArray(response)
        ? response
        : (response?.data || response?.ratings || response?.results || []);
      setRatings(data);
      setOriginalRatings(JSON.parse(JSON.stringify(data)));
      setError(null);
    } catch (err) {
      if (isPermissionDenied(err)) {
        setError(getPermissionDeniedMessage(err));
      } else {
        setError("Failed to load ratings data");
      }
      console.error("Error loading ratings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRatings();
  }, [loadRatings]);


  // Calculate changed rows
  const getChangedRows = useCallback(() => {
    const changes = [];
    ratings.forEach((row, index) => {
      const original = originalRatings[index];
      if (!original) return;

      const rowChanges = [];
      EDITABLE_FIELDS.forEach((field) => {
        const originalVal = original[field] ?? "";
        const currentVal = row[field] ?? "";
        if (String(originalVal) !== String(currentVal)) {
          rowChanges.push({ field, oldValue: originalVal, newValue: currentVal });
        }
      });

      if (rowChanges.length > 0) {
        changes.push({ shortName: row.SHORT_NAME, pm: row.PM, changes: rowChanges, fullRow: row });
      }
    });
    return changes;
  }, [ratings, originalRatings]);

  const hasChanges = useCallback(() => {
    return getChangedRows().length > 0;
  }, [getChangedRows]);

  const handleExport = () => {
    if (!ratings.length) return;

    const exportRows = ratings.map((row) => {
      const output = {};
      columns.forEach((col) => {
        let value = row[col.field] ?? "";
        if (col.field === "MODIFIED_DATE") value = formatDate(value);
        if (col.field === "MODIFIED_BY") value = formatUserName(value);
        output[col.headerName] = value;
      });
      return output;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ratings");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ratings-${dayjs().format("YYYY-MM-DD")}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Cell click - start editing
  const handleCellClick = (rowIndex, field, value) => {
    const editable = EDITABLE_FIELDS.includes(field);

    if (!editable) return;

    // Inline edit
    setEditingCell({ rowIndex, field });
    setEditValue(value ?? "");
  };

  // Save inline edit
  const handleSaveEdit = () => {
    if (!editingCell) return;
    const { rowIndex, field } = editingCell;

    let newValue = editValue === "" ? null : editValue;
    if ((field === "WAL" || field === "SPREAD") && newValue !== null) {
      newValue = parseFloat(newValue);
      if (isNaN(newValue)) newValue = null;
    }

    setRatings(prev => prev.map((r, idx) => idx === rowIndex ? {
      ...r,
      [field]: newValue,
      MODIFIED_BY: user || "unknown",
      MODIFIED_DATE: dayjs().format("YYYY-MM-DD"),
    } : r));
    setEditingCell(null);
    setEditValue("");
  };

  // Cancel inline edit
  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue("");
  };

  // Submit changes
  const handleSubmitClick = () => {
    setChangedRows(getChangedRows());
    setDiffDialogOpen(true);
  };

  const handleConfirmSubmit = async () => {
    setDiffDialogOpen(false);
    setSaving(true);
    try {
      const rowsToSave = changedRows.map(c => c.fullRow);
      await saveRatings(user || "unknown", rowsToSave);
      showNotification(`Saved ${rowsToSave.length} row(s)`, "success");
      await loadRatings();
    } catch (err) {
      if (!notifyPermissionError(showNotification, err)) {
        showNotification("Failed to save", "error");
      }
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscardChanges = () => {
    setRatings(JSON.parse(JSON.stringify(originalRatings)));
  };

  const isCellChanged = (rowIndex, field) => {
    if (!EDITABLE_FIELDS.includes(field)) return false;
    const original = originalRatings[rowIndex];
    const current = ratings[rowIndex];
    if (!original || !current) return false;
    return String(original[field] ?? "") !== String(current[field] ?? "");
  };

  const columns = [
    { field: "PM", headerName: "PM" },
    { field: "SHORT_NAME", headerName: "Asset Class", width: 200 },
    { field: "RATING", headerName: "Rating" },
    { field: "WAL", headerName: "WAL" },
    { field: "SPREAD", headerName: "Spread" },
    { field: "BENCHMARK", headerName: "Benchmark" },
    { field: "MODIFIED_BY", headerName: "Modified By" },
    { field: "MODIFIED_DATE", headerName: "Modified Date" },
  ];

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>;
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5">Weekly Internal Spreads</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleExport}
            startIcon={<Download />}
            sx={{ backgroundColor: "lightgrey", color: "black" }}
            disabled={!ratings.length}
          >
            Export Excel
          </Button>
          {hasChanges() && (
            <Button variant="outlined" color="secondary" onClick={handleDiscardChanges} disabled={saving}>
              Discard
            </Button>
          )}
          <Button variant="contained" onClick={handleSubmitClick} disabled={!hasChanges() || saving}>
            {saving ? "Saving..." : "Submit"}
          </Button>
        </Box>
      </Box>

      {hasChanges() && <Alert severity="info" sx={{ mb: 2 }}>You have unsaved changes.</Alert>}

      {/* Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(col => (
                <TableCell key={col.field} sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                  {col.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ratings.map((row, rowIndex) => {
              return (
                <TableRow key={rowIndex}>
                  {columns.map(col => {
                    const cellValue = row[col.field];
                    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.field === col.field;
                    const changed = isCellChanged(rowIndex, col.field);
                    const editable = EDITABLE_FIELDS.includes(col.field);

                    // Editing mode
                    if (isEditing) {
                      return (
                        <TableCell key={col.field}>
                          <TextField
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleSaveEdit}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit();
                              if (e.key === "Escape") handleCancelEdit();
                            }}
                            autoFocus
                            size="small"
                            variant="standard"
                            type={col.field === "WAL" || col.field === "SPREAD" ? "number" : "text"}
                            fullWidth
                          />
                        </TableCell>
                      );
                    }

                    // Display mode
                    let displayValue = cellValue ?? "";
                    if (col.field === "MODIFIED_DATE") displayValue = formatDate(cellValue);
                    if (col.field === "MODIFIED_BY") displayValue = formatUserName(cellValue);

                    return (
                      <TableCell
                        key={col.field}
                        onClick={() => handleCellClick(rowIndex, col.field, cellValue)}
                        sx={{
                          cursor: editable ? "pointer" : "default",
                          backgroundColor: changed ? "#c8e6c9" : editable ? "#fffef0" : "inherit",
                          fontWeight: changed ? "bold" : "normal",
                          "&:hover": editable ? { backgroundColor: "#fff9c4" } : {},
                          minWidth: col.width || 100,
                        }}
                      >
                        {displayValue}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diff Dialog */}
      <Dialog open={diffDialogOpen} onClose={() => setDiffDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Review Changes</DialogTitle>
        <DialogContent dividers>
          {changedRows.length === 0 ? (
            <Typography>No changes.</Typography>
          ) : (
            changedRows.map((change, idx) => (
              <Paper key={idx} sx={{ p: 2, mb: 2 }} variant="outlined">
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                  {change.shortName} ({change.pm})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {change.changes.map((fc, i) => (
                    <Chip
                      key={i}
                      label={
                        <span>
                          <strong>{fc.field}:</strong>{" "}
                          <span style={{ textDecoration: "line-through", color: "#d32f2f" }}>
                            {fc.oldValue || "(empty)"}
                          </span>{" "}
                          →{" "}
                          <span style={{ color: "#2e7d32" }}>
                            {fc.newValue || "(empty)"}
                          </span>
                        </span>
                      }
                      variant="outlined"
                      sx={{ height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 1 } }}
                    />
                  ))}
                </Box>
              </Paper>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
